/**
 * Error translator for converting backend errors to user-friendly Polish messages
 */

import { isTurnstileErrorCode, TURNSTILE_ERROR_MESSAGE } from "../constants/turnstile";

export interface BackendError {
  message?: string | object;
  error?: string;
  errorCode?: string;
  statusCode?: number;
  details?: unknown;
}

export interface TranslatedError {
  message: string;
  field?: string;
  code?: string;
}

/**
 * Translates backend errors to user-friendly Polish messages
 */
export function translateError(error: any): TranslatedError {
  // Handle axios errors
  if (error.response?.data) {
    const backendError = error.response.data as BackendError;
    const statusCode = error.response.status;

    return translateBackendError(backendError, statusCode);
  }

  // Handle network errors
  if (error.request) {
    return {
      message: "Brak połączenia z serwerem. Sprawdź połączenie internetowe.",
      code: "NETWORK_ERROR",
    };
  }

  // Handle other errors
  return {
    message: "Wystąpił nieoczekiwany błąd. Spróbuj ponownie.",
    code: "UNKNOWN_ERROR",
  };
}

function translateBackendError(
  backendError: BackendError,
  statusCode: number
): TranslatedError {
  // Handle validation errors (400)
  if (statusCode === 400) {
    return translateValidationError(backendError);
  }

  // Handle conflict errors (409) - duplicate subdomain
  if (statusCode === 409) {
    return translateConflictError(backendError);
  }

  // Handle unauthorized errors (401)
  if (statusCode === 401) {
    return {
      message: "Brak uprawnień do wykonania tej operacji.",
      code: "UNAUTHORIZED",
    };
  }

  // Handle Turnstile-specific 403 errors
  if (statusCode === 403) {
    if (backendError.errorCode && isTurnstileErrorCode(backendError.errorCode)) {
      return {
        message: TURNSTILE_ERROR_MESSAGE,
        code: backendError.errorCode,
      };
    }

    return {
      message: "Nie masz uprawnień do wykonania tej operacji.",
      code: "FORBIDDEN",
    };
  }

  // Handle not found errors (404)
  if (statusCode === 404) {
    return {
      message: "Zasób nie został znaleziony.",
      code: "NOT_FOUND",
    };
  }

  // Handle server errors (500+)
  if (statusCode >= 500) {
    return {
      message: "Błąd serwera. Spróbuj ponownie za chwilę.",
      code: "SERVER_ERROR",
    };
  }

  // Generic error based on message
  return translateGenericError(backendError, statusCode);
}

function translateValidationError(backendError: BackendError): TranslatedError {
  const message = extractMessage(backendError);

  // Check for specific validation patterns
  if (message.includes("subdomain")) {
    if (
      message.includes("must be unique") ||
      message.includes("already exists")
    ) {
      return {
        message: "Subdomena jest już zajęta. Wybierz inną subdomenę.",
        field: "subdomain",
        code: "SUBDOMAIN_TAKEN",
      };
    }
    if (
      message.includes("length") ||
      message.includes("too short") ||
      message.includes("too long")
    ) {
      return {
        message: "Subdomena musi mieć od 3 do 63 znaków.",
        field: "subdomain",
        code: "SUBDOMAIN_LENGTH",
      };
    }
    if (message.includes("invalid") || message.includes("format")) {
      return {
        message: "Subdomena może zawierać tylko małe litery, cyfry i myślniki.",
        field: "subdomain",
        code: "SUBDOMAIN_FORMAT",
      };
    }
  }

  if (message.includes("name")) {
    if (message.includes("required") || message.includes("cannot be empty")) {
      return {
        message: "Nazwa tenanta jest wymagana.",
        field: "name",
        code: "NAME_REQUIRED",
      };
    }
    if (
      message.includes("length") ||
      message.includes("too short") ||
      message.includes("too long")
    ) {
      return {
        message: "Nazwa tenanta musi mieć od 2 do 100 znaków.",
        field: "name",
        code: "NAME_LENGTH",
      };
    }
  }

  if (message.includes("email")) {
    if (message.includes("invalid") || message.includes("format")) {
      return {
        message: "Podaj prawidłowy adres email.",
        field: "email",
        code: "EMAIL_FORMAT",
      };
    }
    if (message.includes("required")) {
      return {
        message: "Adres email jest wymagany.",
        field: "email",
        code: "EMAIL_REQUIRED",
      };
    }
  }

  if (message.includes("modules")) {
    return {
      message: "Wybierz co najmniej jeden moduł dla tenanta.",
      field: "enabledModules",
      code: "MODULES_REQUIRED",
    };
  }

  // Generic validation error
  return {
    message: "Sprawdź poprawność wprowadzonych danych i spróbuj ponownie.",
    code: "VALIDATION_ERROR",
  };
}

function translateConflictError(backendError: BackendError): TranslatedError {
  const message = extractMessage(backendError);

  if (message.includes("subdomain")) {
    return {
      message: "Subdomena jest już zajęta. Wybierz inną subdomenę.",
      field: "subdomain",
      code: "SUBDOMAIN_CONFLICT",
    };
  }

  if (message.includes("name")) {
    return {
      message: "Tenant o tej nazwie już istnieje.",
      field: "name",
      code: "NAME_CONFLICT",
    };
  }

  if (message.includes("user")) {
    return {
      message: "Użytkownik jest już przypisany do tego tenanta.",
      field: "user",
      code: "USER_CONFLICT",
    };
  }

  return {
    message: "Zasób już istnieje. Sprawdź wprowadzone dane.",
    code: "CONFLICT",
  };
}

function translateGenericError(
  backendError: BackendError,
  statusCode: number
): TranslatedError {
  const message = extractMessage(backendError);

  // Try to extract meaningful information from the message
  if (message.includes("tenant")) {
    if (message.includes("not found")) {
      return {
        message: "Tenant nie został znaleziony.",
        code: "TENANT_NOT_FOUND",
      };
    }
    if (message.includes("create")) {
      return {
        message:
          "Nie udało się utworzyć tenanta. Sprawdź dane i spróbuj ponownie.",
        code: "TENANT_CREATE_FAILED",
      };
    }
    if (message.includes("update")) {
      return {
        message: "Nie udało się zaktualizować tenanta. Spróbuj ponownie.",
        code: "TENANT_UPDATE_FAILED",
      };
    }
    if (message.includes("delete")) {
      return {
        message: "Nie udało się usunąć tenanta. Spróbuj ponownie.",
        code: "TENANT_DELETE_FAILED",
      };
    }
  }

  if (message.includes("user")) {
    if (message.includes("not found")) {
      return {
        message: "Użytkownik nie został znaleziony.",
        code: "USER_NOT_FOUND",
      };
    }
    if (message.includes("add")) {
      return {
        message: "Nie udało się dodać użytkownika do tenanta.",
        code: "USER_ADD_FAILED",
      };
    }
    if (message.includes("remove")) {
      return {
        message: "Nie udało się usunąć użytkownika z tenanta.",
        code: "USER_REMOVE_FAILED",
      };
    }
  }

  if (message.includes("module")) {
    return {
      message: "Nie udało się zaktualizować modułów tenanta.",
      code: "MODULES_UPDATE_FAILED",
    };
  }

  // Default error messages based on status code
  switch (statusCode) {
    case 400:
      return {
        message: "Nieprawidłowe dane. Sprawdź formularz i spróbuj ponownie.",
        code: "BAD_REQUEST",
      };
    case 409:
      return {
        message: "Konflikt danych. Zasób już istnieje.",
        code: "CONFLICT",
      };
    default:
      return {
        message:
          "Wystąpił błąd podczas wykonywania operacji. Spróbuj ponownie.",
        code: "GENERIC_ERROR",
      };
  }
}

/**
 * Safely extracts message from backend error, handling both string and object cases
 */
function extractMessage(backendError: BackendError): string {
  if (typeof backendError.message === "string") {
    return backendError.message.toLowerCase();
  }

  if (typeof backendError.message === "object" && backendError.message) {
    // Try to extract message from object
    const messageObj = backendError.message as any;
    if (typeof messageObj.message === "string") {
      return messageObj.message.toLowerCase();
    }
    if (Array.isArray(messageObj) && messageObj.length > 0) {
      return String(messageObj[0]).toLowerCase();
    }
    // If object, convert to string representation
    return JSON.stringify(messageObj).toLowerCase();
  }

  if (typeof backendError.error === "string") {
    return backendError.error.toLowerCase();
  }

  return "unknown error";
}

/**
 * Specialized translator for tenant operations
 */
export function translateTenantError(
  error: any,
  operation:
    | "create"
    | "update"
    | "delete"
    | "addUser"
    | "removeUser"
    | "updateModules"
): string {
  const translated = translateError(error);

  // If we have a specific field error message, use it
  if (translated.field) {
    return translated.message;
  }

  // Otherwise, provide operation-specific fallbacks
  const fallbacks = {
    create: "Nie udało się utworzyć tenanta. Sprawdź dane i spróbuj ponownie.",
    update: "Nie udało się zaktualizować tenanta. Spróbuj ponownie.",
    delete: "Nie udało się usunąć tenanta. Spróbuj ponownie.",
    addUser: "Nie udało się dodać użytkownika do tenanta.",
    removeUser: "Nie udało się usunąć użytkownika z tenanta.",
    updateModules: "Nie udało się zaktualizować modułów tenanta.",
  };

  return translated.message || fallbacks[operation];
}
